import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { init, track } from '@plausible-analytics/tracker'
import { KULTURIO_ENV } from '../Settings'
import { AppContext } from '../contexts/AppContext'

// ---- tiny helpers ----
export type CommonProps = Partial<{
  deviceId?: string,
  documentId?: string
  documentType: 'menu' | 'article' | 'mosaic' | 'timeline' | "imagemap" | 'flipbook' | 'gallery' | 'autoplay'
  ownerId: string
  ownerSlug: string
  windowSize: string
  language: string
}>

function defaults(): Pick<CommonProps, 'windowSize' | 'language'> {
  const w = typeof window !== 'undefined' ? window : undefined
  return {
    windowSize: w ? `${w.innerWidth}x${w.innerHeight}` : undefined
  }
}
function clean<T extends Record<string, any>>(obj?: T) {
  if (!obj) return undefined
  const out: Record<string, any> = {}
  for (const [k, v] of Object.entries(obj)) if (v != null) out[k] = v
  return Object.keys(out).length ? (out as T) : undefined
}

export function useInitPlausibleTracker() {
  const initedRef = useRef(false)
  if (!initedRef.current) {
    
    // Development tracking
    if (KULTURIO_ENV === "development") {
      init({
        domain: 'dev-kiosk.kulturio.org',
        captureOnLocalhost: true,
        hashBasedRouting: true,
        autoCapturePageviews: false
      })
    }

    // Production tracking
    if (KULTURIO_ENV === "production") {
      init({
        domain: 'kiosk.kulturio.org',
        captureOnLocalhost: true,
        hashBasedRouting: true,
        autoCapturePageviews: false
      })
    }
    initedRef.current = true
  }
}

export function usePlausiblePageview(props?: CommonProps, opts?: { waitFor?: boolean | (keyof CommonProps)[] | (() => boolean), timeoutMs?: number, overrideUrl?: string }) {
  const location = useLocation()
  const { previewIsActive } = useContext(AppContext);
  const sentKeyRef = useRef<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const baseProps = useMemo(() => clean({ ...defaults(), ...props }), [props])

  const ready = useMemo(() => {
    const w = opts?.waitFor
    if (w === undefined) return true // no waiting -> send immediately
    if (typeof w === 'boolean') return w
    if (Array.isArray(w)) return w.every((k) => (props as any)?.[k] != null)
    if (typeof w === 'function') return !!w()
    return true
  }, [opts?.waitFor, props])

  useEffect(() => {
    if (previewIsActive || baseProps?.deviceId === "undefined") { return }

    const key = `${location.pathname}${location.search}`
    if (sentKeyRef.current === key) return

    const send = () => {
      if (sentKeyRef.current === key) return
      track('pageview', { props: baseProps, url: opts?.overrideUrl })
      sentKeyRef.current = key
    }

    // clear any previous timer on route change
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }

    if (ready) {
      send()
      return
    }

    // Not ready yet: optional timeout fallback
    if (opts?.timeoutMs != null) {
      timerRef.current = window.setTimeout(send, opts.timeoutMs) as unknown as number
    }

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [location.pathname, location.search, baseProps, ready, opts?.timeoutMs, opts?.overrideUrl, previewIsActive])
}

/** Tiny goals helper. */
export function usePlausibleEvent() {
  const goal = useCallback(
    (name: string, props?: CommonProps) =>
      track(name, { props: clean({ ...defaults(), ...props }) }),
    []
  )
  return { goal }
}